/**
 * Hook principal do Welcome Wizard
 * Gerencia estado completo, persistência em localStorage, navegação e ações de dados
 * 
 * API PÚBLICA (publicar no dia 2):
 * - wizard.state: estado completo
 * - wizard.currentStepKey: string da tela atual
 * - wizard.stepIndex: número base 1 para barra de progresso
 * - wizard.template: tema carregado
 * - wizard.goNext() / wizard.goBack() / wizard.rebuildSteps()
 * - wizard.setTheme / setUserName / setLocationName / setProfessionals / setServices / etc
 * - wizard.submit() / wizard.completeAndExit()
 * - wizard.showSkip / wizard.revealSkip() / wizard.skipWizard()
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { loadTemplate } from '../utils/templateLoader';
import { postWelcome, getWelcomeSummary, buildWelcomePayload } from '../services/welcomeService';
import { authService } from '../services/authService';
import { wlog, wsnapshot } from '../utils/welcomeDebug';

const STORAGE_KEY = 'agendario_welcome_session';

/**
 * Monta o array de steps dinamicamente após a Tela 3 (equipe confirmada).
 * skipThemeStep (D7/D8, onda 3): omite a Tela 0 quando o usuário já nasceu com tema
 * (utm_campaign válido no cadastro, 3B.2) — começa direto no step seguinte.
 * Exportada (além do hook) só para ser testável em isolamento — função pura, sem estado.
 */
export const buildSteps = (professionals, hasGroups, skipThemeStep = false) => {
  const isTeam = professionals.length > 1;
  return [
    ...(skipThemeStep ? [] : ['theme']),                 // Tela 0 (omitida se já tem tema)
    'userName',                                          // n=1
    'locationName',                                      // n=2
    'team',                                              // n=3
    'services',                                          // n=4
    'servicePricing',                                    // n=5 (D1)
    ...(isTeam ? professionals.map((_, i) => `assign_${i}`) : []),  // Tela 6 — só equipe
    'groupQuestion',
    ...(hasGroups ? ['groupSizes'] : []),
    ...professionals.map((_, i) => `schedule_${i}`),     // Tela 8
    'processing',
    'summary',
  ];
};

/**
 * Estado inicial do wizard.
 * D7/D8 (onda 3): se o usuário já nasceu com tema (claim do JWT — UserService grava na
 * criação via utm_campaign, 3B.2), pré-popula themeId e pula a Tela 0.
 */
const createInitialState = () => {
  const existingThemeId = authService.getThemeFromToken();
  return {
    themeId: existingThemeId || null,
    customBusinessType: null,
    userName: '',
    locationName: '',
    professionals: [],
    services: [],
    serviceDetails: {},
    hasGroups: false,
    groupSizes: {},
    steps: buildSteps([], false, !!existingThemeId),
    currentStepIndex: 0,
    showSkip: false,
    isProcessing: false,
    result: null,
    error: null,
    completedAt: null,
  };
};

/**
 * Hook principal do Welcome Wizard
 */
export const useWelcomeWizard = () => {
  const [state, setState] = useState(createInitialState);
  const [template, setTemplate] = useState(null);
  const templateLoadedRef = useRef(false);

  // ───── DEBUG: loga TODO render do hook ─────
  wlog('useWelcomeWizard', 'RENDER', {
    ...wsnapshot(state),
    templateLoaded: !!template,
    templateId: template?.id,
  });

  // Carrega o template ao montar
  useEffect(() => {
    wlog('useWelcomeWizard', 'EFEITO template', {
      themeId: state.themeId,
      jaCarregado: templateLoadedRef.current,
    });
    const loadTheme = async () => {
      if (state.themeId && !templateLoadedRef.current) {
        try {
          const tpl = await loadTemplate(state.themeId);
          wlog('useWelcomeWizard', 'template CARREGADO', { themeId: tpl?.id, temWelcome: !!tpl?.welcome });
          setTemplate(tpl);
          templateLoadedRef.current = true;
        } catch (error) {
          wlog('useWelcomeWizard', '⚠️ ERRO ao carregar template', error?.message);
          console.error('[useWelcomeWizard] Erro ao carregar template:', error);
        }
      }
    };
    loadTheme();
  }, [state.themeId]);

  // Restaura estado do localStorage ao montar
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    wlog('useWelcomeWizard', 'EFEITO restore — localStorage', savedState ? JSON.parse(savedState) : 'VAZIO');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.completedAt) {
          wlog('useWelcomeWizard', 'restore IGNORADO (completedAt presente)', parsed.completedAt);
          return;
        }
        if (!parsed.steps || parsed.steps.length === 0) {
          parsed.steps = buildSteps(parsed.professionals || [], parsed.hasGroups || false, !!parsed.themeId);
        }
        wlog('useWelcomeWizard', '⚠️ RESTAURANDO estado salvo (pode pular telas!)', wsnapshot(parsed));
        setState(parsed);
        templateLoadedRef.current = false;
      } catch (error) {
        wlog('useWelcomeWizard', '⚠️ ERRO ao restaurar', error?.message);
        console.error('[useWelcomeWizard] Erro ao restaurar estado:', error);
      }
    }
  }, []);

  // Persiste estado em localStorage a cada mudança
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    wlog('useWelcomeWizard', 'PERSIST → localStorage', wsnapshot(state));
  }, [state]);

  // ========================
  // AÇÕES DE NAVEGAÇÃO
  // ========================

  const goNext = useCallback(() => {
    wlog('useWelcomeWizard', 'goNext() CHAMADO', new Error().stack?.split('\n').slice(1, 4).join(' | '));
    setState((prev) => {
      const nextIndex = prev.currentStepIndex + 1;
      if (nextIndex < prev.steps.length) {
        wlog('useWelcomeWizard', `goNext: ${prev.currentStepIndex} → ${nextIndex}`, {
          de: prev.steps[prev.currentStepIndex],
          para: prev.steps[nextIndex],
        });
        return { ...prev, currentStepIndex: nextIndex };
      }
      wlog('useWelcomeWizard', '⚠️ goNext BLOQUEADO (fim do array)', {
        currentStepIndex: prev.currentStepIndex,
        stepsLen: prev.steps.length,
      });
      return prev;
    });
  }, []);

  const goBack = useCallback(() => {
    wlog('useWelcomeWizard', 'goBack() CHAMADO');
    setState((prev) => {
      // Não volta a partir da Tela 9 (processing/summary)
      if (
        prev.steps[prev.currentStepIndex] === 'processing' ||
        prev.steps[prev.currentStepIndex] === 'summary'
      ) {
        return prev;
      }
      const prevIndex = prev.currentStepIndex - 1;
      if (prevIndex >= 0) {
        return { ...prev, currentStepIndex: prevIndex };
      }
      return prev;
    });
  }, []);

  const rebuildSteps = useCallback(() => {
    setState((prev) => {
      const newSteps = buildSteps(prev.professionals, prev.hasGroups, !prev.steps.includes('theme'));
      return { ...prev, steps: newSteps };
    });
  }, []);

  // ========================
  // AÇÕES DE DADOS
  // ========================

  const setTheme = useCallback((themeId, customBusinessType = null) => {
    wlog('useWelcomeWizard', 'setTheme()', { themeId, customBusinessType });
    setState((prev) => ({
      ...prev,
      themeId,
      customBusinessType,
    }));
    templateLoadedRef.current = false; // força reload do template
  }, []);

  const setUserName = useCallback((name) => {
    wlog('useWelcomeWizard', 'setUserName()', name);
    setState((prev) => {
      // Profissional 0 é sempre o usuário
      const updated = { ...prev, userName: name };
      if (prev.professionals.length > 0) {
        updated.professionals = [
          { ...prev.professionals[0], name },
          ...prev.professionals.slice(1),
        ];
      }
      return updated;
    });
  }, []);

  const setLocationName = useCallback((name) => {
    setState((prev) => ({ ...prev, locationName: name }));
  }, []);

  const setProfessionals = useCallback((list) => {
    setState((prev) => ({
      ...prev,
      professionals: list,
      steps: buildSteps(list, prev.hasGroups, !prev.steps.includes('theme')),
    }));
  }, []);

  const setServices = useCallback((list) => {
    setState((prev) => ({
      ...prev,
      services: list,
      // Reseta os detalhes de serviço ao mudar a lista
      serviceDetails: {},
      groupSizes: {},
    }));
  }, []);

  const setServiceDetail = useCallback((serviceId, details) => {
    setState((prev) => ({
      ...prev,
      serviceDetails: {
        ...prev.serviceDetails,
        [serviceId]: details,
      },
    }));
  }, []);

  const setProfessionalServiceIds = useCallback((profId, serviceIds) => {
    setState((prev) => {
      const updated = { ...prev };
      if (updated.professionals[profId]) {
        updated.professionals[profId] = {
          ...updated.professionals[profId],
          serviceIds,
        };
      }
      return updated;
    });
  }, []);

  const setHasGroups = useCallback((hasGroups) => {
    setState((prev) => {
      const newSteps = buildSteps(prev.professionals, hasGroups, !prev.steps.includes('theme'));
      return {
        ...prev,
        hasGroups,
        steps: newSteps,
      };
    });
  }, []);

  const setGroupSize = useCallback((serviceId, maxGroupSize) => {
    setState((prev) => ({
      ...prev,
      groupSizes: {
        ...prev.groupSizes,
        [serviceId]: maxGroupSize,
      },
    }));
  }, []);

  const setProfessionalSchedule = useCallback((profId, schedule) => {
    setState((prev) => {
      const updated = { ...prev };
      if (updated.professionals[profId]) {
        updated.professionals[profId] = {
          ...updated.professionals[profId],
          schedule,
        };
      }
      return updated;
    });
  }, []);

  // ========================
  // PROCESSAMENTO
  // ========================

  const submit = useCallback(async () => {
    wlog('useWelcomeWizard', 'submit() CHAMADO — estado atual', wsnapshot(state));
    setState((prev) => ({ ...prev, isProcessing: true, error: null }));
    try {
      // Monta o payload e chama a API
      const payload = buildWelcomePayload(state, template);
      wlog('useWelcomeWizard', 'submit() — payload construído', payload);
      wlog('useWelcomeWizard', 'submit() — payload é Promise?', payload instanceof Promise);
      let response = await postWelcome(payload);
      wlog('useWelcomeWizard', 'submit() — resposta da API', response);

      // Se 409 (conflito), tenta recuperar o resumo
      if (response.status === 'conflict') {
        try {
          const summary = await getWelcomeSummary();
          response = { status: 'created', data: summary };
        } catch (error) {
          console.error('[useWelcomeWizard] Erro ao recuperar resumo:', error);
          throw new Error('Falha ao recuperar resumo do welcome');
        }
      }

      // Sucesso — grava resultado
      setState((prev) => ({
        ...prev,
        isProcessing: false,
        result: response.data,
        steps: buildSteps(prev.professionals, prev.hasGroups, !prev.steps.includes('theme')), // garante que 'summary' está lá
        currentStepIndex: prev.steps.indexOf('summary'),
      }));
    } catch (error) {
      console.error('[useWelcomeWizard] Erro ao submeter:', error);
      setState((prev) => ({
        ...prev,
        isProcessing: false,
        error: error.message || 'Erro ao processar welcome',
      }));
    }
  }, [state, template]);

  const completeAndExit = useCallback(() => {
    const completedAt = new Date().toISOString();
    // Write synchronously — setState is async and the useEffect that persists to
    // localStorage would never fire before window.location.href navigates away.
    try {
      const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, completedAt }));

      // Persist the selected theme so ThemeCustomProvider finds it on the next load
      // and doesn't show the legacy ThemeSelectionModal.
      const themeId = current.themeId || state.themeId;
      if (themeId) {
        const userInfo = authService.getUserInfo();
        const themeKey = userInfo?.userId ? `agendario_theme_${userInfo.userId}` : 'agendario_theme';
        localStorage.setItem(themeKey, themeId);
      }
    } catch (_) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ completedAt }));
    }
    setState((prev) => ({ ...prev, completedAt }));
    return true;
  }, [state.themeId]);

  // ========================
  // SKIP (D5)
  // ========================

  const revealSkip = useCallback(() => {
    setState((prev) => ({ ...prev, showSkip: true }));
  }, []);

  const skipWizard = useCallback(() => {
    const completedAt = new Date().toISOString();
    try {
      const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, completedAt }));
      // Even on skip, persist the theme if one was selected
      const themeId = current.themeId || state.themeId;
      if (themeId) {
        const userInfo = authService.getUserInfo();
        const themeKey = userInfo?.userId ? `agendario_theme_${userInfo.userId}` : 'agendario_theme';
        localStorage.setItem(themeKey, themeId);
      }
    } catch (_) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ completedAt }));
    }
    setState((prev) => ({ ...prev, completedAt }));
    return true;
  }, [state.themeId]);

  // ========================
  // COMPUTED
  // ========================

  const currentStepKey = state.steps[state.currentStepIndex] || null;
  const stepIndex =
    currentStepKey === 'processing' || currentStepKey === 'summary'
      ? 100 // Força 100% para essas telas
      : state.currentStepIndex; // Base 1 — theme está no índice 0

  wlog('useWelcomeWizard', 'COMPUTED', {
    currentStepIndex: state.currentStepIndex,
    currentStepKey,
    stepIndex,
    stepsLen: state.steps.length,
  });

  // ========================
  // API PÚBLICA
  // ========================

  return {
    // --- LEITURA DE ESTADO ---
    state,
    currentStepKey,
    stepIndex,
    template,

    // --- AÇÕES DE NAVEGAÇÃO ---
    goNext,
    goBack,
    rebuildSteps,

    // --- AÇÕES DE DADOS ---
    setTheme,
    setUserName,
    setLocationName,
    setProfessionals,
    setServices,
    setServiceDetail,
    setProfessionalServiceIds,
    setHasGroups,
    setGroupSize,
    setProfessionalSchedule,

    // --- PROCESSAMENTO ---
    submit,
    completeAndExit,

    // --- SKIP (D5) ---
    showSkip: state.showSkip,
    revealSkip,
    skipWizard,
  };
};
